import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Pagination,
  Skeleton,
} from "@mui/material";
import PdfPreviewModal from "../../components/PdfPreviewModal";
import AppDropdown from "../../components/AppDropdown";
import {
  Search as SearchIcon,
  Download as DownloadIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  DescriptionOutlined as DescriptionOutlinedIcon,
  ArrowUpward,
  ArrowDownward,
  ExpandLess as ExpandLessIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import ConfirmModal from "../../components/common/ConfirmModal";
import ChallanViewDialog from "./ChallanViewDialog";
import { challanApi } from "../../api/challanApi";
import { BASE_URL, API_ENDPOINTS } from "../../config/apiconfig";
import backgroundPattern from "../../assets/pasbookpng.png";
import { useColorMode } from "../../contexts/ColorModeContext";

const AnimatedSearchIcon = ({ loading = false }) => (
  <svg
    className={loading ? "search-loading" : ""}
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    style={{ color: "#94A3B8" }}
  >
    <circle cx="11" cy="11" r="7" />
    <line x1="16.65" y1="16.65" x2="21" y2="21" />
  </svg>
);

const AnimatedRefreshIcon = ({ size = 18 }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <g>
      <path d="M21 12a9 9 0 1 1-2.64-6.36" />
      <polyline points="21 3 21 9 15 9" />
      <animateTransform
        attributeName="transform"
        attributeType="XML"
        type="rotate"
        from="0 12 12"
        to="360 12 12"
        dur="1s"
        repeatCount="1"
        fill="freeze" />
    </g>
  </svg>
);

function ViewChallan() {
  const navigate = useNavigate();
  const location = useLocation();
  const { mode } = useColorMode();
  const isDark = mode === 'dark';
  const [generateDropdownOpen, setGenerateDropdownOpen] = useState(false);
  const generateDropdownRef = useRef(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [challans, setChallans] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [size, setSize] = useState(10);
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, challanId: null });
  const [viewModal, setViewModal] = useState({ isOpen: false, challanId: null });
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [backgroundSize, setBackgroundSize] = useState('220px');
  const abortControllerRef = useRef(null);
  const fetchRequestIdRef = useRef(0);

  // Responsive background size
  useEffect(() => {
    const updateBackgroundSize = () => {
      if (window.innerWidth <= 480) {
        setBackgroundSize('120px');
      } else if (window.innerWidth <= 768) {
        setBackgroundSize('150px');
      } else {
        setBackgroundSize('220px');
      }
    };

    updateBackgroundSize();
    window.addEventListener('resize', updateBackgroundSize);
    return () => window.removeEventListener('resize', updateBackgroundSize);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (generateDropdownRef.current && !generateDropdownRef.current.contains(event.target)) {
        setGenerateDropdownOpen(false);
      }
    };

    if (generateDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [generateDropdownOpen]);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const [filters, setFilters] = useState({
    searchType: "name", // "name" or "challanNumber"
    name: "",
    challanNumber: "",
    challanType: "",
    fromDate: "",
    toDate: ""
  });

  const fetchChallans = useCallback(async () => {
    const requestId = ++fetchRequestIdRef.current;
    try {
      setLoading(true);
      
      const token = localStorage.getItem("token");
      
      if (!token) {
        console.error("No authentication token found");
        setChallans([]);
        return;
      }

      const params = new URLSearchParams();

      if (filters.searchType === "name" && filters.name) params.append("name", filters.name);
      if (filters.searchType === "challanNumber" && filters.challanNumber) params.append("challanNumber", filters.challanNumber);
      if (filters.challanType && filters.challanType !== "ALL") params.append("challanType", filters.challanType);
      if (filters.fromDate) params.append("fromDate", filters.fromDate);
      if (filters.toDate) params.append("toDate", filters.toDate);

      params.append("page", page);
      params.append("size", size);

      const res = await fetch(
        `${BASE_URL}${API_ENDPOINTS.CHALLANS.SEARCH}?${params.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          },
          signal: abortControllerRef.current?.signal
        }
      );

      if (!res.ok) {
        throw new Error(`API error: ${res.status}`);
      }

      const data = await res.json();

      // Ignore this response if a newer request has since been fired
      if (requestId !== fetchRequestIdRef.current) return;

      console.log('API Response:', data);
      console.log('Sample challan data:', data.content?.[0] || data[0]);
      
      let sortedData = data.content || data || [];
      
      setChallans(sortedData);
      setTotalPages(data.totalPages || 0);
      setTotalElements(data.totalElements || 0);
      
      const currentPage = data.number !== undefined ? data.number : (data.currentPage !== undefined ? data.currentPage : page);
      setPage(currentPage);

    } catch (error) {
      if (error.name !== 'AbortError' && requestId === fetchRequestIdRef.current) {
        console.error("Fetch error:", error);
        toast.error("Failed to load challans");
        setChallans([]);
      }
    } finally {
      if (requestId === fetchRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [page, size, filters]);

  useEffect(() => {
    const debounceTimeout = setTimeout(() => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      fetchChallans();
    }, 400);

    return () => {
      clearTimeout(debounceTimeout);
    };
  }, [page, size, filters, fetchChallans]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const challanType = searchParams.get("challanType");
    if (challanType) {
      setFilters(prev => ({ ...prev, challanType }));
    }
  }, [location.search]);

  useEffect(() => {
    const quickSearch = location.state?.quickSearch;
    if (!quickSearch) return;

    const searchValue = String(quickSearch).trim();
    if (!searchValue) return;

    // Detect if the search value looks like a challan number (numeric, contains CH/CHAL prefix, or OGI format)
    const isChallanNumber = /^[\d]+$/.test(searchValue) || /^CH/i.test(searchValue) || /^CHAL/i.test(searchValue) || /^OGI/i.test(searchValue);

    setFilters(prev => ({
      ...prev,
      searchType: isChallanNumber ? 'challanNumber' : 'name',
      name: isChallanNumber ? '' : searchValue,
      challanNumber: isChallanNumber ? searchValue : '',
    }));
    setPage(0);
    setFiltersOpen(true);
    // Clear location.state after applying to prevent re-triggering
    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, location.pathname, navigate]);

  // Apply sorting to existing data when sort changes
  const sortedChallans = useMemo(() => {
    console.log('useMemo called - sortField:', sortField, 'sortOrder:', sortOrder, 'challans length:', challans.length);
    
    if (sortField === 'date' && challans.length > 0) {
      console.log('Applying sort to existing data, order:', sortOrder);
      
      const sortedData = [...challans].sort((a, b) => {
        const parseDate = (dateStr) => {
          if (!dateStr) return new Date(0);
          const date = new Date(dateStr);
          return isNaN(date.getTime()) ? new Date(0) : date;
        };
        
        const dateA = parseDate(a.date);
        const dateB = parseDate(b.date);
        
        if (sortOrder === 'asc') {
          return dateA.getTime() - dateB.getTime();
        } else {
          return dateB.getTime() - dateA.getTime();
        }
      });
      
      console.log('Sorted data:', sortedData.map(c => ({ id: c.id, date: c.date })));
      return sortedData;
    }
    console.log('Returning unsorted data');
    return challans;
  }, [sortField, sortOrder, challans]);

  const handleFilterChange = useCallback((field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({
      searchType: "name",
      name: "",
      challanNumber: "",
      challanType: "",
      fromDate: "",
      toDate: ""
    });
    setPage(0);
  }, []);

  const hasActiveFilters = filters.name || filters.challanNumber || filters.challanType || filters.fromDate || filters.toDate;

  const handleSort = useCallback((field) => {
    console.log('handleSort called with field:', field, 'current sortField:', sortField, 'current sortOrder:', sortOrder);
    
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc'); // Default to descending (newest first) for date
    }
    setPage(0);
  }, [sortField, sortOrder]);

  const handleDelete = useCallback((id) => {
    setConfirmModal({ isOpen: true, challanId: id });
  }, []);

  const confirmDelete = useCallback(async () => {
    try {
      await challanApi.deleteChallanById(confirmModal.challanId);
      toast.success("Challan deleted successfully");
      setConfirmModal({ isOpen: false, challanId: null });
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();
      fetchChallans();
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete challan");
      setConfirmModal({ isOpen: false, challanId: null });
    }
  }, [confirmModal.challanId, fetchChallans]);

  const cancelDelete = useCallback(() => {
    setConfirmModal({ isOpen: false, challanId: null });
  }, []);

  const handleDownload = async (id) => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${BASE_URL}/challans/download/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Download failed');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      setPdfUrl(url);
      setPdfModalOpen(true);
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to load challan PDF");
    }
  };

  const handleView = useCallback((id) => {
    console.log('handleView called with ID:', id);
    setViewModal({ isOpen: true, challanId: id });
  }, []);

  const handleEdit = useCallback((challan) => {
    const type = challan.challanType?.toLowerCase();
    if (type === 'delivered') {
      navigate(`/dashboard/challans/delivered/edit/${challan.id}`);
    } else if (type === 'received') {
      navigate(`/dashboard/challans/received/edit/${challan.id}`);
    } else if (type === 'combined') {
      navigate(`/dashboard/challans/combined/edit/${challan.id}`);
    } else {
      navigate(`/dashboard/challans/edit/${challan.id}`);
    }
  }, [navigate]);

  const handlePageChange = useCallback((event, value) => {
    setPage(value - 1);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, []);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    await fetchChallans();
    setTimeout(() => setIsRefreshing(false), 500);
  }, [fetchChallans]);

  return (
    <div style={{ position: 'relative', padding: '8px 4px 36px', minHeight: '100%', background: 'var(--page-bg)' }}>
      <div
        style={{
          position: 'fixed',
          inset: '-30%',
          backgroundImage: `url(${backgroundPattern})`,
          backgroundRepeat: 'repeat',
          backgroundSize: backgroundSize,
          transform: 'rotate(-12deg)',
          opacity: isDark ? 0.025 : 0.08,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <ChallanViewDialog
        open={viewModal.isOpen}
        onClose={() => setViewModal({ isOpen: false, challanId: null })}
        challanId={viewModal.challanId}
      />
      <Box sx={{ position: 'relative', zIndex: 1 }}>
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        title="Delete Challan"
        message="Are you sure you want to delete this challan? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Page Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 1,
              bgcolor: "#4338CA",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              boxShadow: "0 8px 20px rgba(67,56,202,.18)"
            }}
          >
            <DescriptionOutlinedIcon sx={{ fontSize: 26 }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: "38px",
                fontWeight: 700,
                color: isDark ? "#F8FAFC" : "#0F172A",
                lineHeight: 1.1
              }}
            >
              Challans
            </Typography>
            <Typography
              sx={{
                mt: 0.5,
                color: isDark ? "#94A3B8" : "#64748B",
                fontSize: 15
              }}
            >
              Manage delivery, receipt, and combined challans
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', position: 'relative' }}>
          <Button variant="outlined" startIcon={isRefreshing ? <AnimatedRefreshIcon size={18} /> : <RefreshIcon />} onClick={handleRefresh} disabled={loading} sx={{
            height: 48,
            borderRadius: "10px",
            fontWeight: 600,
            padding: "0 20px",
          }}>
            Refresh
          </Button>
          <Box sx={{ position: 'relative' }} ref={generateDropdownRef}>
            <Button
              variant="contained"
              onClick={() => setGenerateDropdownOpen(!generateDropdownOpen)}
              sx={{
                height: 48,
                borderRadius: "10px",
                fontWeight: 600,
                padding: "0 20px",
              }}
            >
              Generate Challan
            </Button>
            {generateDropdownOpen && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 'calc(100% + 6px)',
                  right: 0,
                  zIndex: 99999,
                  background: isDark ? '#1E293B' : '#fff',
                  border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`,
                  borderRadius: '12px',
                  boxShadow: isDark ? '0 12px 28px -8px rgba(0, 0, 0, 0.4)' : '0 12px 28px -8px rgba(15, 23, 42, 0.12)',
                  minWidth: '200px',
                  py: 1,
                }}
                onMouseLeave={() => setGenerateDropdownOpen(false)}
              >
              <Button
                fullWidth
                onClick={() => {
                  navigate('/dashboard/challans/delivered');
                  setGenerateDropdownOpen(false);
                }}
                sx={{
                  justifyContent: 'flex-start',
                  px: 2,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 500,
                  color: isDark ? '#86EFAC' : '#15803d',
                  border: `1px solid ${isDark ? 'rgba(74, 222, 128, 0.2)' : '#dcfce7'}`,
                  backgroundColor: isDark ? 'transparent' : '#fff',
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(74, 222, 128, 0.12)' : '#dcfce7',
                  },
                }}
              >
                Delivered Challan
              </Button>
              <Button
                fullWidth
                onClick={() => {
                  navigate('/dashboard/challans/received');
                  setGenerateDropdownOpen(false);
                }}
                sx={{
                  justifyContent: 'flex-start',
                  px: 2,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 500,
                  color: isDark ? '#60A5FA' : '#2563eb',
                  border: `1px solid ${isDark ? 'rgba(96, 165, 250, 0.2)' : '#dbeafe'}`,
                  backgroundColor: isDark ? 'transparent' : '#fff',
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(96, 165, 250, 0.12)' : '#dbeafe',
                  },
                }}
              >
                Received Challan
              </Button>
              <Button
                fullWidth
                onClick={() => {
                  navigate('/dashboard/challans/combined');
                  setGenerateDropdownOpen(false);
                }}
                sx={{
                  justifyContent: 'flex-start',
                  px: 2,
                  py: 1.5,
                  textTransform: 'none',
                  fontWeight: 500,
                  color: isDark ? '#FBBF24' : '#b45309',
                  border: `1px solid ${isDark ? 'rgba(251, 191, 36, 0.2)' : '#fef3c7'}`,
                  backgroundColor: isDark ? 'transparent' : '#fff',
                  '&:hover': {
                    backgroundColor: isDark ? 'rgba(251, 191, 36, 0.12)' : '#fef3c7',
                  },
                }}
              >
                Combined Challan
              </Button>
            </Box>
          )}
          </Box>
        </Box>
      </Box>

      {/* Filters */}
      {!filtersOpen ? (
        <Button
          variant="outlined"
          startIcon={<AnimatedSearchIcon loading={loading} />}
          onClick={() => setFiltersOpen(true)}
          sx={{
            mb: 3,
            height: 48,
            borderRadius: "10px",
            fontWeight: 600,
            textTransform: "none",
            fontSize: "14px",
            borderColor: isDark ? "#475569" : "#E5E7EB",
            color: isDark ? "#94A3B8" : "#64748b",
            "&:hover": {
              borderColor: "#2563eb",
              background: isDark ? "rgba(37, 99, 235, 0.1)" : "#eff6ff",
              color: "#2563eb",
            },
          }}
        >
          Show Filters
        </Button>
      ) : (
        <Card
          sx={{
            mb: 3,
            borderRadius: "22px",
            border: isDark ? "1px solid #334155" : "1px solid #E5E7EB",
            boxShadow: isDark ? "0 10px 30px rgba(0,0,0,.3)" : "0 10px 30px rgba(15,23,42,.05)",
            overflow: "visible",
            background: isDark ? '#1E293B' : "#fff"
          }}
        >
          <CardContent
            sx={{
              p: { xs: 2, sm: 2.5, md: 3 },
              "&:last-child": {
                pb: { xs: 2, sm: 2.5, md: 3 }
              }
            }}
          >
            {/* Header row */}
            <Box sx={{
              display: "flex",
              alignItems: { xs: "center", sm: "flex-start" },
              justifyContent: "space-between",
              mb: { xs: 2, sm: 2.5 },
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 2, sm: 0 }
            }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, cursor: "pointer" }} onClick={() => setFiltersOpen(false)}>
                <Box sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: isDark ? "rgba(37, 99, 235, 0.2)" : "#eff6ff",
                  color: "#2563eb",
                }}>
                  <SearchIcon sx={{ fontSize: 17 }} />
                </Box>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: isDark ? "#F8FAFC" : "#0f172a" }}>
                  Filters
                </Typography>
                <ExpandLessIcon sx={{ fontSize: 20, color: isDark ? "#94A3B8" : "#64748b" }} />
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  variant="text"
                  size="small"
                  startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
                  onClick={clearFilters}
                  disabled={!hasActiveFilters}
                  sx={{
                    height: 36,
                    mt: { xs: 0, sm: 0.5 },
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "12.5px",
                    textTransform: "none",
                    color: isDark ? "#94A3B8" : "#64748b",
                    "&:hover": {
                      background: isDark ? "#334155" : "#f1f5f9",
                      color: isDark ? "#F8FAFC" : "#334155",
                    },
                    "&:disabled": {
                      color: isDark ? "#475569" : "#cbd5e1",
                      background: "transparent",
                      "&:hover": {
                        background: "transparent",
                        color: isDark ? "#475569" : "#cbd5e1",
                      }
                    }
                  }}
                >
                  Clear Filters
                </Button>
              </Box>
            </Box>

            {/* Fields row */}
            <Box sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(5, 1fr)",
                xl: "repeat(5, 1fr)"
              },
              alignItems: "flex-end",
              gap: { xs: 2, sm: 2, md: 2, lg: 3 }
            }}>
            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: 74 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: isDark ? "#94A3B8" : "#64748B", mb: 0.5 }}>
                Search By
              </Typography>
              <AppDropdown
                value={filters.searchType}
                options={[
                  { value: "name", label: "Customer Name" },
                  { value: "challanNumber", label: "Challan Number" }
                ]}
                onChange={(value) => handleFilterChange("searchType", value)}
                placeholder="Search By"
                position="below"
              />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: 74 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: isDark ? "#94A3B8" : "#64748B", mb: 0.5 }}>
                {filters.searchType === "name" ? "Customer Name" : "Challan Number"}
              </Typography>
              {/* Parent container holding both the search bar and the dropdown */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                {/* Search input wrapper with flex: 1 to fill available space safely */}
                <Box sx={{ position: "relative", flex: 1, height: "40px" }}>
                  <Box sx={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", zIndex: 1 }}>
                    <AnimatedSearchIcon loading={loading || isSearchFocused || (filters.searchType === "name" ? !!filters.name : !!filters.challanNumber)} />
                  </Box>
                  <input
                    type="text"
                    placeholder={filters.searchType === "name" ? "Search customer..." : "Search challan number..."}
                    value={filters.searchType === "name" ? filters.name : filters.challanNumber}
                    onChange={(e) => handleFilterChange(filters.searchType === "name" ? "name" : "challanNumber", e.target.value)}
                    onFocus={() => setIsSearchFocused(true)}
                    onBlur={() => setIsSearchFocused(false)}
                    style={{
                      width: "100%",
                      height: "100%",
                      padding: "0 14px 0 42px",
                      border: isDark ? "1px solid #475569" : "1px solid #E2E8F0",
                      borderRadius: "6px",
                      outline: "none",
                      fontSize: "14px",
                      color: isDark ? "#E2E8F0" : "#0F172A",
                      backgroundColor: "transparent",
                      boxSizing: "border-box"
                    }}
                  />
                </Box>
                {/* Your "All Types" Dropdown component goes here */}
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: 74 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: isDark ? "#94A3B8" : "#64748B", mb: 0.5 }}>
                Challan Type
              </Typography>
              <AppDropdown
                value={filters.challanType || "ALL"}
                options={[
                  { value: "ALL", label: "All Types" },
                  { value: "DELIVERED", label: "Delivered" },
                  { value: "RECEIVED", label: "Received" },
                  { value: "COMBINED", label: "Combined" }
                ]}
                onChange={(value) => handleFilterChange("challanType", value)}
                placeholder="All Types"
                position="below"
              />
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: 74 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: isDark ? "#94A3B8" : "#64748B", mb: 0.5 }}>
                From Date
              </Typography>
              <Box sx={{
                position: "relative",
                width: "100%",
                height: 46,
                borderRadius: "12px",
                backgroundColor: isDark ? "#334155" : "#F8FAFC",
                border: isDark ? "1px solid #475569" : "1px solid #E2E8F0",
                transition: "all .25s ease",
                display: "flex",
                alignItems: "center",
                "&:hover": {
                  backgroundColor: isDark ? "#475569" : "#FFFFFF",
                  borderColor: isDark ? "#64748B" : "#CBD5E1",
                },
                "&:focus-within": {
                  backgroundColor: isDark ? "#475569" : "#FFFFFF",
                  borderColor: "#2563EB",
                  borderWidth: "2px",
                }
              }}>
                <input
                  type="date"
                  value={filters.fromDate}
                  onChange={(e) => handleFilterChange("fromDate", e.target.value)}
                  style={{
                    width: "100%",
                    height: "100%",
                    padding: "0 14px",
                    border: "none",
                    outline: "none",
                    fontSize: "14px",
                    color: isDark ? "#E2E8F0" : "#0F172A",
                    backgroundColor: "transparent",
                    boxSizing: "border-box",
                    fontFamily: "inherit"
                  }}
                />
              </Box>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", height: 74 }}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: isDark ? "#94A3B8" : "#64748B", mb: 0.5 }}>
                To Date
              </Typography>
              <Box sx={{
                position: "relative",
                width: "100%",
                height: 46,
                borderRadius: "12px",
                backgroundColor: isDark ? "#334155" : "#F8FAFC",
                border: isDark ? "1px solid #475569" : "1px solid #E2E8F0",
                transition: "all .25s ease",
                display: "flex",
                alignItems: "center",
                "&:hover": {
                  backgroundColor: isDark ? "#475569" : "#FFFFFF",
                  borderColor: isDark ? "#64748B" : "#CBD5E1",
                },
                "&:focus-within": {
                  backgroundColor: isDark ? "#475569" : "#FFFFFF",
                  borderColor: "#2563EB",
                  borderWidth: "2px",
                }
              }}>
                <input
                  type="date"
                  value={filters.toDate}
                  onChange={(e) => handleFilterChange("toDate", e.target.value)}
                  style={{
                    width: "100%",
                    height: "100%",
                    padding: "0 14px",
                    border: "none",
                    outline: "none",
                    fontSize: "14px",
                    color: isDark ? "#E2E8F0" : "#0F172A",
                    backgroundColor: "transparent",
                    boxSizing: "border-box",
                    fontFamily: "inherit"
                  }}
                />
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
      )}

      {/* Table */}
      <Box sx={{
        backgroundColor: isDark ? '#1E293B' : 'white',
        borderRadius: '8px',
        boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.5)' : '0 2px 4px rgba(0,0,0,0.1)',
        padding: '20px',
        marginBottom: '20px',
        boxSizing: 'border-box',
        maxWidth: '100%',
        overflow: 'visible',
        border: isDark ? '1px solid #334155' : 'none'
      }}>
        <TableContainer
          sx={{
            minHeight: 500,
            maxWidth: '100%',
            width: '100%',
            overflowX: 'auto',
            overflowY: 'auto',
            "&::-webkit-scrollbar": {
              width: 8,
              height: 8,
            },
            "&::-webkit-scrollbar-thumb": {
              borderRadius: 20,
              background: isDark ? "#475569" : "#cbd5e1",
            },
          }}
        >
            <Table sx={{
              minWidth: { xs: 1200, lg: '100%' },
              width: '100%',
              tableLayout: { lg: 'auto', xs: 'fixed' }
            }}>
              <TableHead sx={{
                '& th': { position: 'sticky', top: 0, zIndex: 5, background: isDark ? '#1E293B' : 'white' }
              }}>
                <TableRow>
                  <TableCell align="center" sx={{ width: 220, fontWeight: 700, backgroundColor: isDark ? '#334155' : '#F8FAFC', color: isDark ? '#F8FAFC' : '#0F172A', fontSize: 13, padding: { xs: '12px 10px', md: '16px' } }}>
                    Customer Name
                  </TableCell>
                  <TableCell align="center" sx={{ width: 150, fontWeight: 700, backgroundColor: isDark ? '#334155' : '#F8FAFC', color: isDark ? '#F8FAFC' : '#0F172A', fontSize: 13, padding: { xs: '12px 10px', md: '16px' } }}>
                    Challan No
                  </TableCell>
                  <TableCell align="center" sx={{ width: 150, fontWeight: 700, backgroundColor: isDark ? '#334155' : '#F8FAFC', color: isDark ? '#F8FAFC' : '#0F172A', fontSize: 13, padding: { xs: '12px 10px', md: '16px' } }}>
                    Type
                  </TableCell>
                  <TableCell align="center" sx={{ width: 220, fontWeight: 700, backgroundColor: isDark ? '#334155' : '#F8FAFC', color: isDark ? '#F8FAFC' : '#0F172A', fontSize: 13, padding: { xs: '12px 10px', md: '16px' } }}>
                    Location
                  </TableCell>
                  <TableCell align="center" sx={{ width: 100, fontWeight: 700, backgroundColor: isDark ? '#334155' : '#F8FAFC', color: isDark ? '#F8FAFC' : '#0F172A', fontSize: 13, padding: { xs: '12px 10px', md: '16px' } }}>
                    Qty
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{
                      width: 140,
                      fontWeight: 700,
                      backgroundColor: isDark ? '#334155' : '#F8FAFC',
                      color: isDark ? '#F8FAFC' : '#0F172A',
                      fontSize: 13,
                      padding: { xs: '12px 10px', md: '16px' },
                      cursor: 'pointer',
                      userSelect: 'none',
                      '&:hover': {
                        backgroundColor: isDark ? '#475569' : '#F1F5F9'
                      }
                    }}
                    onClick={() => handleSort('date')}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      Date
                      {sortField === 'date' && (
                        sortOrder === 'asc' ? <ArrowUpward sx={{ fontSize: 16 }} /> : <ArrowDownward sx={{ fontSize: 16 }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center" sx={{ width: 180, fontWeight: 700, backgroundColor: isDark ? '#334155' : '#F8FAFC', color: isDark ? '#F8FAFC' : '#0F172A', fontSize: 13, padding: { xs: '12px 10px', md: '16px' } }}>
                    Created By
                  </TableCell>
                  <TableCell align="center" sx={{
                    width: 220,
                    minWidth: 220,
                    fontWeight: 700,
                    backgroundColor: isDark ? '#334155' : '#F8FAFC',
                    color: isDark ? '#F8FAFC' : '#0F172A',
                    fontSize: 13,
                    padding: { xs: '12px 8px', md: '16px 8px' },
                  }}>
                    <Box sx={{ 
                      width: 188, 
                      maxWidth: '100%', 
                      mx: 'auto', 
                      textAlign: 'center',
                      backgroundColor: 'transparent' // <- fix
                    }}>
                      Actions
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  Array.from({ length: size }).map((_, index) => (
                    <TableRow
                      key={index}
                      sx={{
                        height: 72,
                        "& td": {
                          py: 2,
                          px: 2,
                          verticalAlign: "middle",
                        },
                      }}
                    >
                      {/* Customer */}
                      <TableCell align="center">
                        <Skeleton
                          variant="text"
                          width="80%"
                          height={22}
                          sx={{ mx: "auto" }}
                        />
                      </TableCell>

                      {/* Challan Number */}
                      <TableCell align="center">
                        <Skeleton
                          variant="text"
                          width={120}
                          height={22}
                          sx={{ mx: "auto" }}
                        />
                      </TableCell>

                      {/* Type */}
                      <TableCell align="center">
                        <Skeleton
                          variant="rounded"
                          width={90}
                          height={30}
                          sx={{ borderRadius: "999px", mx: "auto" }}
                        />
                      </TableCell>

                      {/* Location */}
                      <TableCell align="center">
                        <Skeleton
                          variant="text"
                          width="75%"
                          height={22}
                          sx={{ mx: "auto" }}
                        />
                      </TableCell>

                      {/* Qty */}
                      <TableCell align="center">
                        <Skeleton
                          variant="text"
                          width={40}
                          height={22}
                          sx={{ mx: "auto" }}
                        />
                      </TableCell>

                      {/* Date */}
                      <TableCell align="center">
                        <Skeleton
                          variant="text"
                          width={90}
                          height={22}
                          sx={{ mx: "auto" }}
                        />
                      </TableCell>

                      {/* Created By */}
                      <TableCell align="center">
                        <Skeleton
                          variant="text"
                          width={120}
                          height={22}
                          sx={{ mx: "auto" }}
                        />
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            gap: 0.5,
                          }}
                        >
                          {[1, 2, 3].map((item) => (
                            <Skeleton
                              key={item}
                              variant="circular"
                              width={36}
                              height={36}
                            />
                          ))}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : challans.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={8}
                      align="center"
                      sx={{
                        p: 0,
                        borderBottom: "none"
                      }}
                    >
                      <Box
                        sx={{
                          minHeight: 420,
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          py: 6,
                        }}
                      >
                        <Card
                          elevation={0}
                          sx={{
                            width: 420,
                            maxWidth: "90%",
                            borderRadius: 4,
                            border: isDark ? "1px solid #334155" : "1px solid #E2E8F0",
                            background: isDark ? '#1E293B' : "#FFFFFF",
                            textAlign: "center",
                            p: 5,
                            boxShadow: isDark ? "0 12px 32px rgba(0,0,0,.3)" : "0 12px 32px rgba(15,23,42,.06)",
                          }}
                        >
                          <Box
                            sx={{
                              width: 82,
                              height: 82,
                              mx: "auto",
                              mb: 3,
                              borderRadius: "50%",
                              background: isDark ? "rgba(79, 70, 229, 0.2)" : "#EEF2FF",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <DescriptionOutlinedIcon
                              sx={{
                                fontSize: 42,
                                color: "#4F46E5",
                              }}
                            />
                          </Box>

                          <Typography
                            sx={{
                              fontSize: 22,
                              fontWeight: 700,
                              color: isDark ? "#F8FAFC" : "#0F172A",
                            }}
                          >
                            No Challans Found
                          </Typography>

                          <Typography
                            sx={{
                              mt: 1,
                              color: isDark ? "#94A3B8" : "#64748B",
                              fontSize: 14,
                              lineHeight: 1.6,
                            }}
                          >
                            We couldn't find any challans matching your filters.
                            <br />
                            Try clearing the filters or create a new challan.
                          </Typography>

                          <Box
                            sx={{
                              mt: 4,
                              display: "flex",
                              justifyContent: "center",
                              gap: 2,
                              flexWrap: "wrap",
                              position: 'relative',
                            }}
                          >
                            <Button
                              variant="outlined"
                              onClick={clearFilters}
                              disabled={!hasActiveFilters}
                              startIcon={<ClearIcon />}
                              sx={{
                                borderRadius: 3,
                                px: 3,
                                height: 42,
                              }}
                            >
                              Clear Filters
                            </Button>

                            <Box sx={{ position: 'relative' }} ref={generateDropdownRef}>
                              <Button
                                variant="contained"
                                onClick={() => setGenerateDropdownOpen(!generateDropdownOpen)}
                                sx={{
                                  borderRadius: 3,
                                  px: 3,
                                  height: 42,
                                  boxShadow: "0 8px 20px rgba(37,99,235,.25)",
                                }}
                              >
                                Generate Challan
                              </Button>
                              {generateDropdownOpen && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 'calc(100% + 6px)',
                                    right: 0,
                                    zIndex: 99999,
                                    background: isDark ? '#1E293B' : '#fff',
                                    border: isDark ? '1px solid #334155' : '1px solid #e2e8f0',
                                    borderRadius: '12px',
                                    boxShadow: isDark ? '0 12px 28px -8px rgba(0, 0, 0, 0.4)' : '0 12px 28px -8px rgba(15, 23, 42, 0.12)',
                                    minWidth: '200px',
                                    py: 1,
                                  }}
                                  onMouseLeave={() => setGenerateDropdownOpen(false)}
                                >
                                <Button
                                  fullWidth
                                  onClick={() => {
                                    navigate('/dashboard/challans/delivered');
                                    setGenerateDropdownOpen(false);
                                  }}
                                  sx={{
                                    justifyContent: 'flex-start',
                                    px: 2,
                                    py: 1.5,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    color: isDark ? '#86EFAC' : '#15803d',
                                    border: isDark ? '1px solid rgba(74, 222, 128, 0.2)' : '1px solid #dcfce7',
                                    backgroundColor: isDark ? 'transparent' : '#fff',
                                    '&:hover': {
                                      backgroundColor: isDark ? 'rgba(74, 222, 128, 0.12)' : '#dcfce7',
                                    },
                                  }}
                                >
                                  Delivered Challan
                                </Button>
                                <Button
                                  fullWidth
                                  onClick={() => {
                                    navigate('/dashboard/challans/received');
                                    setGenerateDropdownOpen(false);
                                  }}
                                  sx={{
                                    justifyContent: 'flex-start',
                                    px: 2,
                                    py: 1.5,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    color: isDark ? '#60A5FA' : '#2563eb',
                                    border: isDark ? '1px solid rgba(96, 165, 250, 0.2)' : '1px solid #dbeafe',
                                    backgroundColor: isDark ? 'transparent' : '#fff',
                                    '&:hover': {
                                      backgroundColor: isDark ? 'rgba(96, 165, 250, 0.12)' : '#dbeafe',
                                    },
                                  }}
                                >
                                  Received Challan
                                </Button>
                                <Button
                                  fullWidth
                                  onClick={() => {
                                    navigate('/dashboard/challans/combined');
                                    setGenerateDropdownOpen(false);
                                  }}
                                  sx={{
                                    justifyContent: 'flex-start',
                                    px: 2,
                                    py: 1.5,
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    color: isDark ? '#FBBF24' : '#b45309',
                                    border: isDark ? '1px solid rgba(251, 191, 36, 0.2)' : '1px solid #fef3c7',
                                    backgroundColor: isDark ? 'transparent' : '#fff',
                                    '&:hover': {
                                      backgroundColor: isDark ? 'rgba(251, 191, 36, 0.12)' : '#fef3c7',
                                    },
                                  }}
                                >
                                  Combined Challan
                                </Button>
                              </Box>
                            )}
                            </Box>
                          </Box>
                        </Card>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  sortedChallans.map((challan) => (
                    <TableRow
                      key={challan.id}
                      hover
                      sx={{
                        '&:hover': {
                          backgroundColor: isDark ? '#334155' : '#F8FBFF',
                          transform: 'translateX(2px)'
                        },
                        height: 64,
                        '& td': { padding: { xs: '12px 10px', md: '16px' }, verticalAlign: 'middle', color: isDark ? '#E2E8F0' : '#0F172A' },
                        transition: 'background-color .2s ease, transform .2s ease'
                      }}
                  >
                    <TableCell align="center">
                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: isDark ? '#E2E8F0' : '#0F172A' }} align="center">
                          {challan.customerName || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" fontWeight={600} sx={{ color: isDark ? '#E2E8F0' : '#0F172A' }} align="center">
                          {challan.challanNumber || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={challan.challanType || "—"}
                          sx={{
                            padding: { xs: '4px 10px', md: '6px 14px' },
                            borderRadius: '999px',
                            fontSize: { xs: '11px', md: '12px' },
                            fontWeight: 700,
                            letterSpacing: '.3px',
                            height: 'auto',
                            ...(challan.challanType === 'DELIVERED' && { background: '#DCFCE7', color: '#15803D' }),
                            ...(challan.challanType === 'RECEIVED' && { background: '#DBEAFE', color: '#2563EB' }),
                            ...(challan.challanType === 'COMBINED' && { background: '#FEF3C7', color: '#D97706' }),
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: isDark ? '#E2E8F0' : '#0F172A' }} align="center">
                          {challan.siteLocation || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap', color: isDark ? '#E2E8F0' : '#0F172A' }} align="center">
                          {(() => {
                            if (!challan.items || !Array.isArray(challan.items) || challan.items.length === 0) return "—";
                            
                            let totalQty = 0;
                            challan.items.forEach(item => {
                              if (challan.challanType === 'DELIVERED') {
                                totalQty += Number(item.deliveredQty) || 0;
                              } else if (challan.challanType === 'RECEIVED') {
                                totalQty += Number(item.receivedQty) || 0;
                              } else {
                                // COMBINED - sum both
                                totalQty += (Number(item.deliveredQty) || 0) + (Number(item.receivedQty) || 0);
                              }
                            });
                            
                            return totalQty > 0 ? totalQty : "—";
                          })()}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ whiteSpace: 'nowrap', color: isDark ? '#E2E8F0' : '#0F172A' }} align="center">
                          {challan.date || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: isDark ? '#E2E8F0' : '#0F172A' }} align="center">
                          {challan.createdBy || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" sx={{ width: 220, minWidth: 220, padding: { xs: '12px 8px', md: '16px 8px' } }}>
                        <Box sx={{
                          width: 188,
                          maxWidth: '100%',
                          mx: 'auto',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: .25,
                          flexWrap: 'nowrap',
                        }}>
                          <IconButton
                            onClick={() => handleView(challan.id)}
                            sx={{
                              color: '#2563EB',
                              width: { xs: 30, md: 36 }, height: { xs: 30, md: 36 }, borderRadius: '10px',
                              '&:hover': { background: isDark ? '#334155' : '#F8FAFC', transform: 'translateY(-2px)' }
                            }}
                          >
                            <ViewIcon sx={{ fontSize: { xs: '15px', md: '20px' } }} />
                          </IconButton>
                          <IconButton
                            onClick={() => handleEdit(challan)}
                            sx={{
                              color: '#F59E0B',
                              width: { xs: 30, md: 36 }, height: { xs: 30, md: 36 }, borderRadius: '10px',
                              '&:hover': { background: isDark ? '#334155' : '#F8FAFC', transform: 'translateY(-2px)' }
                            }}
                          >
                            <EditIcon sx={{ fontSize: { xs: '15px', md: '20px' } }} />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDownload(challan.id)}
                            sx={{
                              color: '#10B981',
                              width: { xs: 30, md: 36 }, height: { xs: 30, md: 36 }, borderRadius: '10px',
                              '&:hover': { background: isDark ? '#334155' : '#F8FAFC', transform: 'translateY(-2px)' }
                            }}
                          >
                            <DownloadIcon sx={{ fontSize: { xs: '15px', md: '20px' } }} />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDelete(challan.id)}
                            sx={{
                              color: '#EF4444',
                              width: { xs: 30, md: 36 }, height: { xs: 30, md: 36 }, borderRadius: '10px',
                              '&:hover': { background: isDark ? '#334155' : '#F8FAFC', transform: 'translateY(-2px)' }
                            }}
                          >
                            <DeleteIcon sx={{ fontSize: { xs: '15px', md: '20px' } }} />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
        </TableContainer>
          {/* Pagination */}
          {!loading && totalPages > 0 && (<Box
              sx={{
                display: "flex",
                flexDirection: {
                  xs: "column",
                  md: "row",
                },
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
                p: 2,
                borderTop: "1px solid",
                borderColor: isDark ? '#334155' : 'divider',
              }}
            >
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: { xs: "center", md: "flex-start" }, gap: 1 }}>
                {totalElements > 0 && (
                  <Typography variant="caption" sx={{ color: isDark ? "#94A3B8" : "text.secondary", fontSize: "0.875rem" }}>
                    Showing {page * size + 1}–{Math.min((page + 1) * size, totalElements)} of {totalElements} records
                  </Typography>
                )}
                <Pagination
                count={totalPages}
                page={page + 1}
                onChange={handlePageChange}
                disabled={loading}
                shape="rounded"
                variant="outlined"
                color="primary"
                showFirstButton
                showLastButton
                siblingCount={0}
                boundaryCount={1}
                size="small"
                sx={{
                  "& .MuiPaginationItem-root": {
                    transition: "transform .2s, box-shadow .2s, background-color .2s",
                    borderRadius: "10px",
                    fontWeight: 600,
                    minWidth: 38,
                    height: 38,
                    borderColor: isDark ? "#475569" : undefined,
                    color: isDark ? "#E2E8F0" : undefined,
                  },
                  "& .MuiPaginationItem-root:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: isDark ? "0 4px 10px rgba(0,0,0,.4)" : "0 4px 10px rgba(0,0,0,.15)",
                    backgroundColor: isDark ? "#334155" : undefined,
                  },
                  "& .MuiPaginationItem-root:active": {
                    transform: "scale(.95)",
                  },
                  "& .Mui-selected": {
                    transform: "scale(1.05)",
                    boxShadow: isDark ? "0 6px 14px rgba(37,99,235,.4)" : "0 6px 14px rgba(37,99,235,.25)",
                  },
                  "& .Mui-disabled": {
                    color: isDark ? "#475569" : undefined,
                    borderColor: isDark ? "#334155" : undefined,
                  },
                  "& ul": {
                    justifyContent: {
                      xs: "center",
                      md: "flex-start",
                    },
                  },
                }}
              />
              </Box>
              <Box
                sx={{
                  width: {
                    xs: 150,
                    sm: 130,
                  },
                  transition: "all .2s",
                  "&:hover": {
                    transform: "translateY(-2px)",
                  },
                }}
              >
                <Typography sx={{ fontSize: { xs: "0.8rem", sm: "0.875rem" }, fontWeight: 500, color: isDark ? "#94A3B8" : "#475569", mb: 0.75 }}>Rows per page</Typography>
                <AppDropdown
                  value={size}
                  options={[
                    { value: 5, label: '5' },
                    { value: 10, label: '10' },
                    { value: 25, label: '25' },
                    { value: 50, label: '50' }
                  ]}
                  onChange={(newSize) => {
                    setSize(newSize);
                    setPage(0);
                  }}
                  disabled={loading}
                />
              </Box>
            </Box>)}
          </Box>
      </Box>
      <PdfPreviewModal
        open={pdfModalOpen}
        onClose={() => setPdfModalOpen(false)}
        pdfUrl={pdfUrl}
        fileName={`challan.pdf`}
      />
    </div>
  );
}

export default ViewChallan;
